import { useState, useEffect, useContext } from 'react';
import {
    PieChart,
    Series as PieSeries,
    Label,
    Connector,
    Legend as PieLegend,
    Tooltip as PieTooltip,
    Export as PieExport,
} from 'devextreme-react/pie-chart';
import {
    DataGrid,
    Column,
    FilterRow,
    HeaderFilter,
    LoadPanel,
    Scrolling,
    Paging,
} from "devextreme-react/data-grid";
import { Close as CloseIcon } from "@mui/icons-material";
import { ChartFilterTagBox as TagBox } from './ChartFilterTagBox';
import { Popover } from 'devextreme-react/popover';
import { getLastRefreshTimeFromRawData, handleIRPointClick, STATUS_COLOR_MAP } from '../FSServices';
import { convertUTCTimeToLocal } from '../../../util/Utils';
import { createSitesClientApi } from '../../../api-client-nswag/taxportal-client-runtime';
import { Box, Dialog, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material';
import DatasetLinkedIcon from '@mui/icons-material/DatasetLinked';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { OidcIdentityContext } from '@rsmus/react-auth';

interface Props {
    rawIRData: any[];
    handleRefresh: () => void;
    siteId: number;
}


// ─── Shared Formatters ───────────────────────────────────────────────────────

function customizeIRPieTooltip(arg: { argument: string; value: number; percentText: string }) {
    return { text: `${arg.argument}: ${arg.value} (${arg.percentText})` };
}

function customizeIRPieLabel(arg: { argument: string; percentText: string }) {
    return `${arg.argument}: ${arg.percentText}`;
}

function customizeIRPieLegendText(arg: any) {
    const name = arg?.pointName || arg?.argument || '';
    const value = arg?.pointValueText || arg?.valueText || arg?.value;
    if (value === undefined || value === null || value === '') {
        return name;
    }
    return `${name} (${value})`;
}

// ─── Pie Legend Toggle Hook ──────────────────────────────────────────────────

function useIRPieLegendSliceToggle() {
    const [selectedStatuses, setSelectedStatuses] = useState<Set<string>>(new Set());

    const getLegendStatus = (event: any) => {
        const target = event?.target;
        const status = target?.argument || target?.originalArgument || target?.pointName || target;
        return status !== undefined && status !== null ? String(status) : null;
    };

    const applySelectionToPoints = (nextSelection: Set<string>, component: any) => {
        const series = component?.getAllSeries?.()?.[0];
        if (!series || typeof series.getAllPoints !== 'function') return;

        const hasSelection = nextSelection.size > 0;
        series.getAllPoints().forEach((point: any) => {
            const pointKey = point?.argument !== undefined && point?.argument !== null
                ? String(point.argument)
                : String(point?.originalArgument || '');
            const shouldShow = !hasSelection || nextSelection.has(pointKey);
            shouldShow ? point.show?.() : point.hide?.();
        });
    };

    const handleLegendClick = (event: any) => {
        event.cancel = true;
        const status = getLegendStatus(event);
        if (!status) return;

        setSelectedStatuses((prev) => {
            const next = new Set(prev);
            next.has(status) ? next.delete(status) : next.add(status);
            applySelectionToPoints(next, event?.component);
            return next;
        });
    };

    const customizeLegendItems = (items: any[]) =>
        items.map((item) => {
            const itemKey = item.argument !== undefined && item.argument !== null
                ? String(item.argument)
                : String(item.text || item.pointName || item.name || '');
            if (selectedStatuses.size === 0 || selectedStatuses.has(itemKey)) return item;
            return {
                ...item,
                marker: { ...item.marker, opacity: 0.35 },
                font: { ...item.font, color: '#888b8d' },
            };
        });

    return { selectedStatuses, handleLegendClick, customizeLegendItems };
}

// ─── IR Status Data ──────────────────────────────────────────────────────────

function getIRStatusFilterValues(rawData: any[]): string[] {
    return Array.from(new Set(rawData.map((x: any) => x.status || '').filter(Boolean))).sort();
}

function getIREntityFilterValues(rawData: any[]): string[] {
    return Array.from(new Set(rawData.map((x: any) => x.entity || x.entityName || '').filter(Boolean))).sort();
}

function getIRDueDateValue(item: any) {
    const rawDueDate = item?.dueDate || item?.dueDateText;
    if (!rawDueDate) return '';

    const parsed = new Date(rawDueDate);
    if (Number.isNaN(parsed.getTime())) {
        return String(rawDueDate);
    }

    return parsed.toLocaleDateString('en-US');
}

function getIRInformationRequestValue(item: any) {
    return String(item?.informationRequested || '').trim() || '';
}

function getIRStatusData(rawData: any[]) {
    const grouped: Record<string, number> = {};
    rawData.forEach((x: any) => {
        const status = x.status || '';
        grouped[status] = (grouped[status] || 0) + 1;
    });
    return Object.keys(grouped).map((status) => ({ status, value: grouped[status] }));
}

function customizeIRPiePoint(pointInfo: { argument?: string }) {
    const status = String(pointInfo?.argument || '');
    return { color: STATUS_COLOR_MAP[status] || '#888b8d' };
}


export const IRStatusChart = (props: Props) => {

    const { user } = useContext(OidcIdentityContext);
    const rawData = props.rawIRData;
    const [entityFilter, setEntityFilter] = useState<string[]>([]);
    const [isFilterPopoverOpen, setIsFilterPopoverOpen] = useState(false);
    const [lastRefreshTime, setLastRefreshTime] = useState("");
    const [isDataViewOpen, setIsDataViewOpen] = useState(false);
    const [statusFilter, setStatusFilter] = useState('All');
    const [dueDateFilter, setDueDateFilter] = useState('All');
    const [entityDataFilter, setEntityDataFilter] = useState('All');
    const [informationRequestFilter, setInformationRequestFilter] = useState('All');

    const { selectedStatuses, handleLegendClick, customizeLegendItems } = useIRPieLegendSliceToggle();
    const legendFilteredData = selectedStatuses.size === 0
        ? rawData
        : rawData.filter((item: any) => selectedStatuses.has(String(item.status || '')));
    const entityOptions = getIREntityFilterValues(legendFilteredData);
    const statusOptions = Array.from(new Set(rawData.map((x: any) => x.status || '').filter(Boolean))).sort();
    const dueDateOptions = Array.from(new Set(rawData.map((x: any) => getIRDueDateValue(x)).filter(Boolean))).sort();
    const entityDataOptions = Array.from(new Set(rawData.map((x: any) => x.entity || x.entityName || '').filter(Boolean))).sort();
    const informationRequestOptions = Array.from(new Set(rawData.map((x: any) => getIRInformationRequestValue(x)).filter(Boolean))).sort();

    const filteredData = entityFilter.length === 0
        ? rawData
        : rawData.filter((x: any) => entityFilter.includes(x.entity || x.entityName || ''));

    const dataViewRows = filteredData
        .filter((item: any) => selectedStatuses.size === 0 || selectedStatuses.has(String(item.status || '')))
        .map((item: any) => ({
        status: String(item?.status || '').trim() || '',
        entity: String(item?.entity || item?.entityName || '').trim() || '',
        dueDate: getIRDueDateValue(item),
        informationRequested: getIRInformationRequestValue(item),
        siteName: String(item?.hqSiteName || item?.site || '').trim() || ''
    }));

    const filteredDataViewRows = dataViewRows.filter((row) => {
        const statusMatch = statusFilter === 'All' || row.status === statusFilter;
        const dueDateMatch = dueDateFilter === 'All' || row.dueDate === dueDateFilter;
        const entityMatch = entityDataFilter === 'All' || row.entity === entityDataFilter;
        const informationRequestMatch = informationRequestFilter === 'All' || row.informationRequested === informationRequestFilter;
        return statusMatch && dueDateMatch && entityMatch && informationRequestMatch;
    });

    const chartData = getIRStatusData(filteredData);

    useEffect(() => {
        if (props.siteId === null || props.siteId === undefined) {
            setLastRefreshTime('');
            return;
        }

        const fetchItemTrack = async () => {
            const itemTrack = await createSitesClientApi.taxportalDbData_GetItemSyncTrackByType(props.siteId, "SYNC_IR", user?.profile.preferred_username as string);
            const lastModified = (itemTrack as any)?.data?.lastModified ?? (itemTrack as any)?.lastModified
            const localTime = lastModified?.trim() ? convertUTCTimeToLocal(lastModified): '';
            setLastRefreshTime(localTime);
        };
        fetchItemTrack();
    }, [rawData, props.siteId, user?.profile.preferred_username]);

    return (
        <div>
            <div className="chart-header">
                <div className="chart-title-section1">
                    <div className="chart-title-row1">
                        <h3 className="chart-title1">Information Request - Status Overview</h3>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <div
                                onClick={() => {
                                    setStatusFilter('All');
                                    setDueDateFilter('All');
                                    setEntityDataFilter('All');
                                    setInformationRequestFilter('All');
                                    setIsDataViewOpen(true);
                                }}
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: 32,
                                    height: 32,
                                    borderRadius: 4,
                                    border: '1px solid #d0d0d0',
                                    cursor: 'pointer',
                                    backgroundColor: '#fff',
                                }}
                                title="Data Points"
                            >
                                <DatasetLinkedIcon sx={{ fontSize: 16, color: '#555' }} />
                            </div>
                            <div
                                id="ir-status-filter-icon-trigger"
                                onClick={() => setIsFilterPopoverOpen((curr) => !curr)}
                                className={`fs-chart-filter-trigger${entityFilter.length > 0 ? ' fs-chart-filter-trigger--active' : ''}`}
                                title="Filters"
                            >
                                <i className="dx-icon-filter" style={{ fontSize: 16, color: '#555' }} aria-hidden="true" />
                                {entityFilter.length > 0 && (
                                    <span
                                        style={{
                                            position: 'absolute',
                                            top: -4,
                                            right: -4,
                                            minWidth: 16,
                                            height: 16,
                                            borderRadius: 8,
                                            backgroundColor: '#1976d2',
                                            color: '#fff',
                                            fontSize: 10,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            padding: '0 3px',
                                        }}
                                    >
                                        1
                                    </span>
                                )}
                            </div>
                            <div
                                onClick={props.handleRefresh}
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: 32,
                                    height: 32,
                                    borderRadius: 4,
                                    border: '1px solid #d0d0d0',
                                    cursor: 'pointer',
                                    backgroundColor: '#fff',
                                }}
                                title="Refresh"
                            >  <i
                                    className="dx-icon-refresh"
                                    style={{ fontSize: 16, color: '#555' }}
                                    aria-hidden="true"
                                /></div>
                        </div>
                    </div>
                    <span className="chart-refresh-time1">
                        Last Refreshed: {lastRefreshTime}
                    </span>
                </div>
            </div>

            <Popover
                target="#ir-status-filter-icon-trigger"
                visible={isFilterPopoverOpen}
                onHiding={() => setIsFilterPopoverOpen(false)}
                hideOnOutsideClick={true}
                position="bottom"
                width={280}
            >
                <div className="fs-chart-filter-content" style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 4 }}>
                    <div>
                        <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 4 }}>Entity</div>
                        <TagBox
                            dataSource={entityOptions}
                            value={entityFilter}
                            onValueChanged={(e) => setEntityFilter(e.value ?? [])}
                            placeholder="All entities"
                            showSelectionControls={true}
                            applyValueMode="instantly"
                            searchEnabled={true}
                            width="100%"
                        />
                    </div>
                    {entityFilter.length > 0 && (
                        <button
                            type="button"
                            onClick={() => setEntityFilter([])}
                            style={{
                                fontSize: 12,
                                alignSelf: 'flex-start',
                                background: 'none',
                                border: 'none',
                                color: '#1976d2',
                                cursor: 'pointer',
                                padding: 0,
                            }}
                        >
                            Clear all filters
                        </button>
                    )}
                </div>
            </Popover>

            <Dialog
                open={isDataViewOpen}
                onClose={() => setIsDataViewOpen(false)}
                maxWidth="lg"
                fullWidth
            >
                {/* Header */}
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        px: 2,
                        py: 1,
                        borderBottom: "1px solid #e0e0e0",
                    }}
                >
                    <Typography variant="h6">
                        Information Requests
                    </Typography>

                    <IconButton
                        aria-label="close"
                        onClick={() => setIsDataViewOpen(false)}
                        size="small"
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>

                <DialogContent
                    sx={{
                        p: 2,
                    }}
                >
                    <DataGrid
                        dataSource={dataViewRows}
                        height={420}
                        showBorders
                        rowAlternationEnabled
                        columnAutoWidth
                        wordWrapEnabled
                        hoverStateEnabled
                        noDataText="No data available"
                    >
                        <LoadPanel enabled={false} />

                        <FilterRow visible />

                        <HeaderFilter visible />

                        <Paging
                            enabled
                            defaultPageSize={20}
                        />

                        <Scrolling mode="standard" />

                        <Column dataField="status" caption="Status" />

                        <Column dataField="entity" caption="Entity" />

                        <Column dataField="dueDate" caption="Due Date" />

                        <Column
                            dataField="informationRequested"
                            caption="Information Request"
                        />
                        <Column dataField="siteName" caption="Site"
                        />
                    </DataGrid>
                </DialogContent>
            </Dialog>


            {
                chartData?.length > 0 ? (
                    <PieChart dataSource={chartData} type="doughnut" innerRadius={0.5} resolveLabelOverlapping="shift" customizePoint={customizeIRPiePoint} onLegendClick={handleLegendClick} onPointClick={(event) => handleIRPointClick(event, props.siteId, entityFilter, rawData)}                    >
                        <PieSeries argumentField="status" valueField="value">
                            <Label visible={true} customizeText={customizeIRPieLabel} radialOffset={-20} font={{ color: '#000000' }} backgroundColor="transparent">
                                <Connector visible={true} width={1} />
                            </Label>
                        </PieSeries>
                        <PieLegend
                            verticalAlignment="bottom"
                            horizontalAlignment="center"
                            itemTextPosition="right"
                            markerSize={14}
                            customizeText={customizeIRPieLegendText}
                            customizeItems={customizeLegendItems}
                        />
                        <PieTooltip enabled={true} customizeTooltip={customizeIRPieTooltip} />
                        <PieExport enabled={false} />
                    </PieChart>
                ) : (
                    <div className="no-data-message">
                        No data available
                    </div>
                )
            }
        </div>
    );
};
